// frontend/src/components/dashboard/SiteRecordsPanels.js
//
// The three boxes under the job list: past reconciliations, issues detected,
// and the site directory.
//
// All three were already on the page, and none of them looked like anything.
// Past reconciliations was the words "Open a past reconciliation (23)" set as a
// plain text link below the fold. Issues detected had no presence at all — it
// existed only as a dialog you reached by pressing Review on a job. The site
// directory was a small grey button in a corner. A manager could use this
// screen for a month without knowing two of the three were there.
//
// These take shaped rows and callbacks. They do no fetching and know nothing
// about the API, which is what makes them worth testing.

import React from 'react';

import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';

import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import EmailRounded from '@mui/icons-material/EmailRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import PhoneRounded from '@mui/icons-material/PhoneRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';

import Panel from './Panel';

const LINE = '#e6edea';
const DEEP = '#034f2d';

/** The look of a reconciliation's state, without needing to read the word. */
const STATUS_TONES = {
  approved: { bg: '#e8f6ed', ink: '#0f7a43', label: 'Approved' },
  submitted: { bg: '#eef4ff', ink: '#1d4ed8', label: 'Submitted' },
  rejected: { bg: '#fdecea', ink: '#b3261e', label: 'Rejected' },
  draft: { bg: '#fff4df', ink: '#8a5200', label: 'Draft' },
};

/**
 * A row you can press.
 *
 * Kept to one line of substance and one of detail, because these panels sit
 * three across and a row that wraps to three lines in one of them drags the
 * other two down with it.
 */
function Row({ onClick, children, last }) {
  return (
    <Box
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        font: 'inherit',
        px: 2,
        py: 1.25,
        border: 'none',
        borderBottom: last ? 'none' : `1px solid ${LINE}`,
        background: 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { background: '#f4f9f6' } : {},
      }}
    >
      {children}
    </Box>
  );
}

/** Nothing to show, said in a way that does not read as a failure to load. */
function Empty({ icon, title, detail }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ px: 2, py: 3.5 }}>
      {icon}
      <Typography sx={{ fontWeight: 800, fontSize: 14, textAlign: 'center' }}>{title}</Typography>
      {detail ? (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {detail}
        </Typography>
      ) : null}
    </Stack>
  );
}

function Loading({ rows = 3 }) {
  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      {Array.from({ length: rows }, (unused, index) => (
        <Skeleton key={index} variant="text" height={30} />
      ))}
    </Box>
  );
}

const money = (value) =>
  `£${Math.abs(Number(value) || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * The days already filed.
 *
 * `rows` are shaped by the dashboard: { id, dateLabel, status, variance }.
 */
export function PastReconciliationsPanel({
  rows = [],
  total = 0,
  loading = false,
  onOpen,
  onViewAll,
}) {
  return (
    <Panel
      title="Past reconciliations"
      caption="The most recent days filed for this site"
      right={
        total ? (
          <Chip size="small" label={`${total} filed`} sx={{ fontWeight: 800, bgcolor: '#e8f6ed', color: DEEP }} />
        ) : null
      }
      footerLabel={total ? `Open a past reconciliation (${total})` : 'Open the reconciliation history'}
      onFooterClick={onViewAll}
    >
      {loading ? (
        <Loading />
      ) : !rows.length ? (
        <Empty
          icon={<CheckCircleRounded sx={{ color: '#9db8a8', fontSize: 28 }} />}
          title="Nothing filed yet"
          detail="Submitted days will be listed here."
        />
      ) : (
        rows.map((row, index) => {
          const tone = STATUS_TONES[String(row.status || '').toLowerCase()] || STATUS_TONES.submitted;

          return (
            <Row key={row.id} last={index === rows.length - 1} onClick={onOpen ? () => onOpen(row) : undefined}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14 }} noWrap>
                    {row.dateLabel}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {Number.isFinite(row.variance) && Math.abs(row.variance) >= 0.005
                      ? `Variance ${row.variance < 0 ? '−' : '+'}${money(row.variance)}`
                      : 'No cash variance'}
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  label={tone.label}
                  sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: tone.bg, color: tone.ink, flexShrink: 0 }}
                />
              </Stack>
            </Row>
          );
        })
      )}
    </Panel>
  );
}

/**
 * Cash that has not been explained.
 *
 * This is money, so the figure is the loudest thing in the row — a list of
 * dates with the amounts in grey underneath gave a manager no way to see which
 * one to open first.
 */
export function IssuesPanel({ rows = [], loading = false, onOpen, onViewAll }) {
  const exposure = rows.reduce((sum, row) => sum + Math.abs(Number(row.variance) || 0), 0);

  return (
    <Panel
      title="Issues detected"
      caption="Cash variances over £15 still waiting for sign-off"
      right={
        rows.length ? (
          <Chip size="small" color="error" label={`${rows.length} open`} sx={{ fontWeight: 800 }} />
        ) : (
          <Chip size="small" label="All clear" sx={{ fontWeight: 800, bgcolor: '#e8f6ed', color: DEEP }} />
        )
      }
      footerLabel="Issue audit history"
      onFooterClick={onViewAll}
    >
      {loading ? (
        <Loading />
      ) : !rows.length ? (
        <Empty
          icon={<CheckCircleRounded sx={{ color: '#0f7a43', fontSize: 28 }} />}
          title="No variances waiting"
          detail="Every submitted day balances, or has been signed off."
        />
      ) : (
        <>
          {/* The total first: it is the number the directors ask about. */}
          <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${LINE}`, background: '#fff8f7' }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: '#8c1d18' }}>
              Unexplained in total
            </Typography>
            <Typography
              sx={{ fontWeight: 950, fontSize: 21, color: '#b3261e', fontVariantNumeric: 'tabular-nums' }}
            >
              {money(exposure)}
            </Typography>
          </Box>

          {rows.map((row, index) => (
            <Row key={row.id} last={index === rows.length - 1} onClick={onOpen ? () => onOpen(row) : undefined}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14 }} noWrap>
                    {row.dateLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.variance < 0 ? 'Short in the safe' : 'Over in the safe'}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: 15,
                    color: '#b3261e',
                    fontVariantNumeric: 'tabular-nums',
                    flexShrink: 0,
                  }}
                >
                  {row.variance < 0 ? '−' : '+'}
                  {money(row.variance)}
                </Typography>
              </Stack>
            </Row>
          ))}
        </>
      )}
    </Panel>
  );
}

/**
 * Who to ring.
 *
 * The site's own details first, because the commonest use of this on a site
 * screen is a manager reading their own number off it to somebody on the
 * phone; the rest of the estate is one press away.
 */
export function SiteDirectoryPanel({ site, others = [], loading = false, onViewAll }) {
  const line = (icon, value, fallback) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      {icon}
      <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'} noWrap>
        {value || fallback}
      </Typography>
    </Stack>
  );

  return (
    <Panel
      title="Site directory"
      caption="Contacts for this site and the rest of the estate"
      footerLabel={others.length ? `All sites (${others.length + 1})` : 'Open the site directory'}
      onFooterClick={onViewAll}
      bodySx={{ display: 'flex', flexDirection: 'column' }}
    >
      {loading ? (
        <Loading rows={4} />
      ) : (
        <>
          <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
              <Typography sx={{ fontWeight: 900, fontSize: 15 }} noWrap>
                {site?.name || 'This site'}
              </Typography>
              <Chip
                size="small"
                label="You"
                sx={{ height: 19, fontSize: 10.5, fontWeight: 800, bgcolor: '#e8f6ed', color: DEEP }}
              />
            </Stack>

            <Stack spacing={0.65}>
              {line(<PlaceRounded sx={{ fontSize: 16, color: '#7d9186' }} />, site?.code, 'No site code')}
              {line(
                <PersonRounded sx={{ fontSize: 16, color: '#7d9186' }} />,
                site?.managerName,
                'Manager not assigned'
              )}
              {line(<EmailRounded sx={{ fontSize: 16, color: '#7d9186' }} />, site?.email, 'Email not available')}
              {line(<PhoneRounded sx={{ fontSize: 16, color: '#7d9186' }} />, site?.phone, 'Number not available')}
            </Stack>
          </Box>

          {/*
            The rest of the estate, in the box rather than behind a dialog.

            This panel is one of three in a row and stretches to the tallest of
            them, so without this it was four lines of text and a gap. Sites
            ring each other about deliveries and cover, and the number they
            want was two clicks away.
          */}
          {others.length ? (
            <Box
              sx={{
                borderTop: `1px solid ${LINE}`,
                flex: 1,
                minHeight: 0,
                maxHeight: 210,
                overflowY: 'auto',
              }}
            >
              {others.map((other, index) => (
                <Box
                  key={other.code || other.name || index}
                  sx={{
                    px: 2,
                    py: 0.85,
                    borderBottom: index === others.length - 1 ? 'none' : `1px solid ${LINE}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0 }} noWrap>
                      {other.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {other.phone || '—'}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Box>
          ) : null}
        </>
      )}
    </Panel>
  );
}

export default Panel;
